import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import type { EditorState } from '@tiptap/pm/state';

import { SIGNATURE_NODE_NAME } from './extensions/signatureNode';

/**
 * The nodes the flow model lets carry a rule.
 *
 * `tableCell` is absent deliberately and must stay absent: a table's column widths, colspan
 * occupancy and vertical merges are resolved in index lockstep, so a cell that could disappear
 * would shift every cell right of a merge into the wrong column.
 */
const RULEABLE = new Set([
	'paragraph',
	'listItem',
	'bulletList',
	'orderedList',
	'table',
	'tableRow',
	'image',
	SIGNATURE_NODE_NAME,
]);

/** A row's children are cells, which cannot carry a rule, so a range never stops at a row. */
const isRangeParent = (node: ProseMirrorNode): boolean => node.type.name !== 'tableRow';

const hasRule = (node: ProseMirrorNode): boolean => typeof node.attrs['rule'] === 'string';

/**
 * The textblocks the selection actually covers, as content start/end positions.
 *
 * A block the selection only touches at an edge is left out: Shift+Down ends the selection at
 * the start of the next block, which the author did not select.
 */
const coveredTextblocks = (state: EditorState): { start: number; end: number }[] => {
	const { empty, $from, ranges } = state.selection;
	if (empty) {
		return $from.parent.isTextblock ? [{ start: $from.start(), end: $from.end() }] : [];
	}
	// Every range, not just `from`/`to`: a cell selection is one range per cell, and `from`/`to`
	// only describe the first.
	const covered = new Map<number, { start: number; end: number }>();
	for (const range of ranges) {
		const from = range.$from.pos;
		const to = range.$to.pos;
		state.doc.nodesBetween(from, to, (node, pos) => {
			if (!node.isTextblock) {
				return true;
			}
			const start = pos + 1;
			const end = pos + node.nodeSize - 1;
			const overlaps =
				Math.max(from, start) < Math.min(to, end) ||
				(node.content.size === 0 && from <= start && end <= to);
			if (overlaps) {
				covered.set(start, { start, end });
			}
			return false;
		});
	}
	return [...covered.values()].sort((left, right) => left.start - right.start);
};

/**
 * The blocks a rule is set on: the smallest whole blocks that hold the selection.
 *
 * A caret marks its paragraph; a selection across list items marks those items; one across
 * table cells marks their rows. Never the enclosing list or table unless the selection runs
 * outside it — marking the whole numbered body for a caret in one clause hid every clause.
 */
const targetBlocks = (state: EditorState): { node: ProseMirrorNode; pos: number }[] => {
	const { selection } = state;
	if (selection instanceof NodeSelection) {
		return RULEABLE.has(selection.node.type.name)
			? [{ node: selection.node, pos: selection.from }]
			: [];
	}
	const covered = coveredTextblocks(state);
	const first = covered[0];
	const last = covered.at(-1);
	if (first === undefined || last === undefined) {
		return [];
	}
	const range = state.doc
		.resolve(first.start)
		.blockRange(state.doc.resolve(last.end), isRangeParent);
	if (range === null) {
		return [];
	}
	const targets: { node: ProseMirrorNode; pos: number }[] = [];
	let pos = range.start;
	for (let index = range.startIndex; index < range.endIndex; index += 1) {
		const node = range.parent.child(index);
		if (RULEABLE.has(node.type.name)) {
			targets.push({ node, pos });
		}
		pos += node.nodeSize;
	}
	return targets;
};

/** Every rule that hides some of the selection: on the targets, inside them, or around them. */
const rulesOverSelection = (state: EditorState): { node: ProseMirrorNode; pos: number }[] => {
	const found = new Map<number, ProseMirrorNode>();
	const targets = targetBlocks(state);
	for (const target of targets) {
		const $pos = state.doc.resolve(target.pos);
		for (let depth = $pos.depth; depth > 0; depth -= 1) {
			const ancestor = $pos.node(depth);
			if (hasRule(ancestor)) {
				found.set($pos.before(depth), ancestor);
			}
		}
		if (hasRule(target.node)) {
			found.set(target.pos, target.node);
		}
		target.node.descendants((node, offset) => {
			if (hasRule(node)) {
				found.set(target.pos + 1 + offset, node);
			}
			return true;
		});
	}
	return [...found].map(([pos, node]) => ({ node, pos }));
};

/** Whether removing a rule from the selection would change anything. */
export const selectionHasRule = (editor: Editor): boolean =>
	rulesOverSelection(editor.state).length > 0;

/**
 * Marks the selection with one rule, or with `null` removes every rule that hides any of it.
 *
 * Returns how many blocks actually CHANGED, so the caller can tell "unmarked three clauses"
 * from "nothing you had selected was marked" and say so rather than appearing to do nothing.
 * Removing clears enclosing rules too — content under a ruled list is hidden by that rule, so
 * leaving it would make "now shows for everyone" untrue.
 */
export const setSelectionRule = (editor: Editor, ruleId: string | null): number => {
	const transaction = editor.state.tr;
	const blocks = ruleId === null ? rulesOverSelection(editor.state) : targetBlocks(editor.state);
	let changed = 0;
	for (const { node, pos } of blocks) {
		// Counting only what actually MOVES: `setNodeMarkup` registers a step whether or not the
		// attributes differ, so applying a rule twice would otherwise report success.
		if ((node.attrs['rule'] ?? null) === ruleId) {
			continue;
		}
		transaction.setNodeMarkup(pos, undefined, { ...node.attrs, rule: ruleId });
		changed += 1;
	}
	if (changed > 0) {
		editor.view.dispatch(transaction);
	}
	return changed;
};

/**
 * Unmarks every block belonging to any of `ruleIds`.
 *
 * Deleting a rule has to take its markings with it: content left pointing at a rule nobody
 * defines is refused by the save and by generation, so leaving it behind would turn "remove this
 * rule" into a document that cannot be stored.
 */
export const clearRules = (editor: Editor, ruleIds: ReadonlySet<string>): void => {
	const { state } = editor;
	const transaction = state.tr;
	let cleared = 0;

	state.doc.descendants((node: ProseMirrorNode, pos: number) => {
		const rule: unknown = node.attrs['rule'];
		if (typeof rule === 'string' && ruleIds.has(rule)) {
			transaction.setNodeMarkup(pos, undefined, { ...node.attrs, rule: null });
			cleared += 1;
		}
		return true;
	});

	if (cleared > 0) {
		editor.view.dispatch(transaction);
	}
};
